import { useId, useState } from 'react';

import { listSiteCmsImagesApi, uploadSiteCmsImageApi } from '@/lib/api/siteCmsApi';
import type { SiteCmsImageResponse, SiteCmsOwnerType } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SiteCmsDialog } from './SiteCmsDialog';
import SiteCmsPrivateImage from './SiteCmsPrivateImage';
import SiteCmsStorageQuota from './SiteCmsStorageQuota';
import { cmsImageFileIssue, cmsInsertUploadedImage, cmsSearchMatches } from './site-cms-interaction-model';
import { cmsBytes, cmsStorageFileIssue } from './site-cms-storage-model';
import { useCmsBusyOperation, useCmsInteractions, useCmsPendingForm } from './use-cms-interactions';
import { cmsMediaUsageKey, useCmsMediaUsage } from './use-cms-media-usage';

type Props = {
  ownerType: SiteCmsOwnerType;
  ownerId: number;
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  fallback?: string;
};
export default function SiteCmsImagePicker(props: Props) {
  const [open, setOpen] = useState(false);
  return (
    <div className='cms-image-field'>
      <span className='cms-field-label'>{props.label ?? 'Image'}</span>
      <div className='cms-image-selection'>
        {props.value ? (
          <SiteCmsPrivateImage ownerType={props.ownerType} ownerId={props.ownerId} id={props.value} alt='' />
        ) : (
          <span className='cms-media-placeholder'>{props.fallback ?? 'Sans image : présentation du thème'}</span>
        )}
        <div className='cms-row'>
          <button type='button' className='cms-button' onClick={() => setOpen(true)}>
            {props.value ? 'Changer l’image' : 'Choisir ou importer'}
          </button>
          {props.value && (
            <button type='button' className='cms-link-button' onClick={() => props.onChange(null)}>
              {props.fallback ? 'Reprendre le réglage du site' : 'Retirer ici'}
            </button>
          )}
        </div>
      </div>
      {open && <ImageChooser {...props} onClose={() => setOpen(false)} />}
    </div>
  );
}
function ImageChooser({ ownerType, ownerId, value, onChange, onClose }: Props & { onClose: () => void }) {
  const instance = useId();
  const key = ['site-cms', 'images', ownerType, ownerId];
  const client = useQueryClient();
  const { ask } = useCmsInteractions();
  const usage = useCmsMediaUsage(ownerType, ownerId);
  const quota = usage.data?.images;
  const quotaUnavailable = usage.isError || !quota;
  const query = useQuery({ queryKey: key, queryFn: () => listSiteCmsImagesApi(ownerType, ownerId), retry: false });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(value);
  const [page, setPage] = useState(0);
  const [inputKey, setInputKey] = useState(0);
  const [notice, setNotice] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [alt, setAlt] = useState('');
  const upload = useMutation({
    mutationFn: (source: { file: File; title: string; alt: string }) =>
      uploadSiteCmsImageApi(ownerType, ownerId, source.title, source.alt, source.file),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: cmsMediaUsageKey(ownerType, ownerId) });
    },
    onSuccess: image => {
      client.setQueryData<SiteCmsImageResponse[]>(key, rows => cmsInsertUploadedImage(rows, image));
      void client.invalidateQueries({ queryKey: key });
      setSelected(image.id);
      setFile(null);
      setTitle('');
      setAlt('');
      setSearch('');
      setPage(0);
      setInputKey(before => before + 1);
      setNotice('Image importée dans la bibliothèque privée. Cliquez sur « Utiliser cette image » pour l’ajouter ici.');
    },
  });
  useCmsPendingForm(`Import d’image:${instance}`, Boolean(file || title || alt));
  useCmsBusyOperation(`image-picker:${instance}`, upload.isPending);
  const issue = cmsImageFileIssue(file, query.data?.length ?? 0) ?? cmsStorageFileIssue('images', file, quota);
  async function close() {
    if (upload.isPending) return;
    if (
      (file || title || alt) &&
      (await ask({
        title: 'Fermer l’import ?',
        message: 'Les champs non envoyés seront abandonnés. Une image déjà importée reste dans la bibliothèque privée.',
        choices: [{ value: 'close', label: 'Fermer sans importer', danger: true }],
      })) !== 'close'
    )
      return;
    onClose();
  }
  async function choose() {
    if (upload.isPending || query.isError || !query.data?.some(image => image.id === selected)) return;
    if (
      (file || title || alt) &&
      (await ask({
        title: 'Utiliser l’image sélectionnée ?',
        message:
          'Votre import non envoyé sera abandonné. L’image sélectionnée remplacera uniquement cette occurrence du brouillon.',
        choices: [{ value: 'use', label: 'Utiliser sans importer' }],
      })) !== 'use'
    )
      return;
    onChange(selected);
    onClose();
  }
  const rows = (query.data ?? []).filter(image => cmsSearchMatches(`${image.title} ${image.alt}`, search));
  const pages = Math.max(1, Math.ceil(rows.length / 24));
  const currentPage = Math.min(page, pages - 1);
  const selection = query.data?.find(image => image.id === selected);
  return (
    <SiteCmsDialog wide title='Choisir une image' onClose={() => void close()} busy={upload.isPending}>
      <p className='cms-hint'>Le choix remplace uniquement cette occurrence du brouillon. Rien n’est publié.</p>
      <label className='cms-field-label'>
        Rechercher dans la bibliothèque
        <input
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            setPage(0);
          }}
        />
      </label>
      {notice && (
        <p className='cms-notice' role='status'>
          {notice}
        </p>
      )}
      {selection && (
        <p className='cms-selection-summary'>
          Sélection : <strong>{selection.title}</strong>
        </p>
      )}
      {query.isPending ? (
        <p role='status'>Chargement de la bibliothèque…</p>
      ) : query.isError ? (
        <p role='alert' className='cms-alert'>
          Bibliothèque indisponible.{' '}
          <button type='button' onClick={() => void query.refetch()}>
            Réessayer
          </button>
        </p>
      ) : (
        <>
          <div className='cms-picker-grid'>
            {rows.slice(currentPage * 24, (currentPage + 1) * 24).map(image => (
              <button
                type='button'
                key={image.id}
                className={selected === image.id ? 'is-selected' : ''}
                aria-pressed={selected === image.id}
                onClick={() => setSelected(image.id)}
              >
                <SiteCmsPrivateImage ownerType={ownerType} ownerId={ownerId} id={image.id} alt='' />
                <strong>{image.title}</strong>
                <small>
                  {image.width} × {image.height}
                </small>
              </button>
            ))}
          </div>
          {!rows.length && (
            <div className='cms-empty' role='status'>
              <p>
                {search ? 'Aucune image ne correspond à cette recherche.' : 'Importez votre première image ci-dessous.'}
              </p>
              {search && (
                <button
                  type='button'
                  className='cms-link-button'
                  onClick={() => {
                    setSearch('');
                    setPage(0);
                  }}
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          )}
          {rows.length > 24 && (
            <nav className='cms-pagination' aria-label='Pages de la bibliothèque'>
              <button
                type='button'
                className='cms-button'
                disabled={!currentPage}
                onClick={() => setPage(currentPage - 1)}
              >
                Précédent
              </button>
              <span role='status'>
                Page {currentPage + 1} sur {pages} · {rows.length} images
              </span>
              <button
                type='button'
                className='cms-button'
                disabled={currentPage + 1 >= pages}
                onClick={() => setPage(currentPage + 1)}
              >
                Suivant
              </button>
            </nav>
          )}
        </>
      )}
      <SiteCmsStorageQuota
        kind='images'
        quota={quota}
        loading={usage.isPending}
        failed={usage.isError}
        refreshing={usage.isFetching}
        retry={() => void usage.refetch()}
      />
      <details className='cms-import-details' open={Boolean(file)}>
        <summary>Importer une nouvelle image</summary>
        <fieldset
          disabled={upload.isPending || query.isPending || query.isError || quotaUnavailable}
          className='cms-form-grid'
        >
          <label>
            Fichier JPEG, PNG ou WebP
            <input
              key={inputKey}
              type='file'
              accept='image/jpeg,image/png,image/webp'
              aria-invalid={Boolean(issue)}
              aria-describedby={issue ? `${instance}-file-error` : undefined}
              onChange={event => {
                const source = event.target.files?.[0] ?? null;
                setFile(source);
                setNotice('');
                upload.reset();
                if (source) setTitle(source.name.replace(/\.[^.]+$/, '').slice(0, 150));
              }}
            />
          </label>
          <label>
            Titre
            <input value={title} maxLength={150} onChange={event => setTitle(event.target.value)} />
          </label>
          <label className='cms-span-two'>
            Description alternative
            <input value={alt} maxLength={250} onChange={event => setAlt(event.target.value)} />
          </label>
          <p className='cms-hint'>
            25 mégapixels maximum. Optimisation automatique en WebP.
            {file ? ` Fichier sélectionné : ${cmsBytes(file.size)}.` : ''}
          </p>
          {issue && (
            <p id={`${instance}-file-error`} role='alert' className='cms-alert cms-span-two'>
              {issue}
            </p>
          )}
          <button
            type='button'
            className='cms-button'
            disabled={!file || !title.trim() || Boolean(issue)}
            onClick={() => {
              if (file && !issue) upload.mutate({ file, title: title.trim(), alt: alt.trim() });
            }}
          >
            {upload.isPending ? 'Optimisation…' : 'Importer dans la bibliothèque'}
          </button>
        </fieldset>
      </details>
      {upload.isError && (
        <p role='alert' className='cms-alert'>
          {upload.error.message}
        </p>
      )}
      {selected && !query.isPending && !query.isError && !selection && (
        <p role='alert' className='cms-alert'>
          L’image sélectionnée n’est plus disponible.
        </p>
      )}
      <footer className='cms-dialog-actions'>
        <button type='button' className='cms-button' disabled={upload.isPending} onClick={() => void close()}>
          Annuler
        </button>
        <button
          type='button'
          className='cms-button cms-button-primary'
          disabled={upload.isPending || query.isError || !selection}
          onClick={() => void choose()}
        >
          Utiliser cette image
        </button>
      </footer>
    </SiteCmsDialog>
  );
}
